import { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      // Set loading to false immediately so UI renders. Role will update asynchronously.
      setLoading(false);

      if (currentUser) {
        // 1. Immediate local check for admin email
        const normalizedEmail = (currentUser.email || "").toLowerCase().trim();
        const adminEmails = ["achhalkar1995@gmail.com", "hrbynature@gmail.com"];
        const isAdminEmail = adminEmails.includes(normalizedEmail);

        // Optimistically set role if email matches
        if (isAdminEmail) {
          console.log("Immediate Admin Access Granted for:", normalizedEmail);
          setRole("admin");
        } else {
          // Temporary role until DB fetch
          setRole("user");
        }

        // 2. Background Sync with Backend (MySQL)
        const syncUserRole = async () => {
          try {
            const syncResponse = await fetch("http://localhost:5000/api/users/sync", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                uid: currentUser.uid,
                email: normalizedEmail,
                role: isAdminEmail ? "admin" : "user"
              })
            });

            if (syncResponse.ok) {
              const data = await syncResponse.json();
              // IMPORTANT: Only update if the user is still the same one (prevent race condition on logout)
              if (auth.currentUser && auth.currentUser.uid === currentUser.uid) {
                if (!isAdminEmail) {
                  setRole(data.role);
                }
              }
            }
          } catch (error) {
            console.warn("Backend sync failed:", error);
          }
        };

        syncUserRole();
      } else {
        setRole(null);
      }
    });

    return unsubscribe;
  }, []);

  const logout = async () => {
    try {
      await auth.signOut();
      setRole(null);
      setUser(null);
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, role, loading, logout }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
};
